import React, { useState, useRef } from "react";
import { DatePicker, Select } from "antd";
import moment from "moment";
import { addFilterToUrl, removeFilterFromUrl, getFilterFromUrl } from "@/utils/utils";

const { RangePicker } = DatePicker;

export const DATE_KEYS = {
  createdAt: "createdAt",
  postedAt: "postedAt",
  pickedUpAt: "pickedUpAt",
  droppedOffAt: "droppedOffAt",
  pickedUpETA: "pickedUpETA",
  droppedOffETA: "droppedOffETA",
};

const dateOptions = [
  {
    value: DATE_KEYS.createdAt,
    label: "Created at",
  },
  {
    value: DATE_KEYS.postedAt,
    label: "Posted at",
  },
  {
    value: DATE_KEYS.pickedUpAt,
    label: "Picked up at",
  },
  {
    value: DATE_KEYS.droppedOffAt,
    label: "Dropped off at",
  },
  {
    value: DATE_KEYS.pickedUpETA,
    label: "Pickup ETA",
  },
  {
    value: DATE_KEYS.droppedOffETA,
    label: "Dropoff ETA",
  },
];

const DATE_DEFAULT_FORMAT = ["MM/DD/YY", "MM/DD/YY"];

function DateRangeFilter({ style = {}, onChangeFilters }) {
  const [filterType, setFilterType] = useState(DATE_KEYS.postedAt);
  const [calendarState, setCalendarState] = useState({
    calendarOpen: false,
    selectOpen: false,
    calendarChange: false,
    selectChange: false,
  });

  const calendarRef = useRef();

  const afterDateChange = () => {
    setCalendarState(prevState => ({
      ...prevState,
      calendarChange: false,
    }));

    calendarRef.current.focus();
  };

  const handleDateChange = dates => {
    if (!dates || (!dates[0] && !dates[1])) {
      removeFilterFromUrl(filterType);
      afterDateChange();
      onChangeFilters?.({ [filterType]: undefined });
      return;
    }

    const newDateRange = `${dates[0].format("YYYY-MM-DD")}/${dates[1].format("YYYY-MM-DD")}`;

    addFilterToUrl(filterType, newDateRange);
    afterDateChange();
    onChangeFilters?.({ [filterType]: newDateRange });
  };

  const getDatesValues = () => {
    const filter = getFilterFromUrl(filterType);

    if (!filter) {
      return [];
    }

    const [start, end] = (filter || "").split("/");

    if (!start || !end) {
      return [];
    }

    return [start, end].map(date => moment(date, "YYYY-MM-DD"));
  };

  return (
    <RangePicker
      ref={calendarRef}
      style={style}
      format={DATE_DEFAULT_FORMAT}
      open={calendarState.calendarOpen}
      onFocus={() =>
        setCalendarState(prevState => ({
          ...prevState,
          calendarOpen: true,
        }))
      }
      onBlur={() =>
        setCalendarState(prevState => {
          if (prevState.selectOpen) {
            return { ...prevState };
          }

          if (prevState.calendarChange) {
            return {
              ...prevState,
              calendarChange: false,
            };
          }

          return {
            calendarChange: false,
            calendarOpen: false,
            selectOpen: false,
            selectChange: false,
          };
        })
      }
      onCalendarChange={(_, dateStrings) => {
        if (dateStrings[0] || dateStrings[1]) {
          setCalendarState(prevState => ({
            ...prevState,
            calendarChange: true,
          }));
        }
      }}
      onChange={handleDateChange}
      placeholder={["Start", "End"]}
      value={getDatesValues()}
      renderExtraFooter={() => (
        <Select
          open={calendarState.selectOpen}
          style={{ width: "100%" }}
          value={filterType}
          onClick={() => {
            setCalendarState(prevState => {
              if (prevState.selectChange) {
                return {
                  ...prevState,
                  selectChange: false,
                };
              }

              return {
                ...prevState,
                calendarOpen: true,
                selectOpen: !prevState.selectOpen,
              };
            });
            calendarRef.current.focus();
          }}
          onBlur={() => {
            setCalendarState(prevState => {
              if (prevState.selectChange) {
                return {
                  ...prevState,
                  selectOpen: false,
                };
              }

              return prevState;
            });
          }}
          onChange={value => {
            setFilterType(value);
            setCalendarState(prevState => ({
              ...prevState,
              selectChange: true,
            }));
            calendarRef.current.focus();
          }}
        >
          {dateOptions.map(option => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      )}
    />
  );
}

export default DateRangeFilter;
